import React, { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Loader2, Printer, ExternalLink, Image as ImageIcon, MessageCircle, Share2 } from "lucide-react";
import { toast } from "sonner";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

// Render halaman PDF ke JPEG Full HD (sisi terpanjang 1920px).
const renderFullHd = async (doc, p) => {
  const page = await doc.getPage(p);
  const vp1 = page.getViewport({ scale: 1 });
  const vp = page.getViewport({ scale: 1920 / Math.max(vp1.width, vp1.height) });
  const c = document.createElement("canvas");
  c.width = Math.round(vp.width); c.height = Math.round(vp.height);
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, c.width, c.height);
  await page.render({ canvasContext: ctx, viewport: vp }).promise;
  return new Promise((res) => c.toBlob(res, "image/jpeg", 0.95));
};

const slug = (s) => (s || "dokumen").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

// Pratinjau dokumen (PDF) via PDF.js -> canvas (tampil di semua browser termasuk HP/Safari).
// Tersedia Unduh JPEG + Kirim ke WhatsApp (gambar per halaman). items: [{label,url,filename}|{label,onClick}].
export default function ExportPreview({ open, onClose, title = "Pratinjau Dokumen", pdfUrl, items = [], onPrint, wide = false, toolbar = null }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [sharing, setSharing] = useState(false);
  const containerRef = useRef(null);
  const blobUrlRef = useRef(null);
  const canvasesRef = useRef([]);
  const pdfRef = useRef(null);

  useEffect(() => {
    if (!open || !pdfUrl) return;
    let cancelled = false;
    let pdfDoc = null;
    setLoading(true); setError(false); canvasesRef.current = [];

    (async () => {
      try {
        const r = await api.get(pdfUrl, { responseType: "blob" });
        if (cancelled) return;
        blobUrlRef.current = URL.createObjectURL(r.data);
        const buf = await r.data.arrayBuffer();
        if (cancelled) return;
        pdfDoc = await pdfjsLib.getDocument({ data: buf }).promise;
        pdfRef.current = pdfDoc;
        if (cancelled) return;

        const host = containerRef.current;
        if (!host) return;
        host.innerHTML = "";
        const dpr = window.devicePixelRatio || 1;
        const targetW = (host.clientWidth || (wide ? 1080 : 740)) - 8;

        for (let p = 1; p <= pdfDoc.numPages; p++) {
          if (cancelled) return;
          const page = await pdfDoc.getPage(p);
          const vp1 = page.getViewport({ scale: 1 });
          const scale = targetW / vp1.width;
          const vp = page.getViewport({ scale: scale * dpr });
          const canvas = document.createElement("canvas");
          canvas.width = vp.width; canvas.height = vp.height;
          canvas.style.width = "100%"; canvas.style.height = "auto";
          canvas.className = "mx-auto mb-3 rounded shadow-sm bg-white";
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, canvas.width, canvas.height);
          host.appendChild(canvas);
          await page.render({ canvasContext: ctx, viewport: vp }).promise;
          canvasesRef.current.push(canvas);
        }
        if (!cancelled) setLoading(false);
      } catch (e) {
        if (!cancelled) { setError(true); setLoading(false); toast.error(apiErr(e)); }
      }
    })();

    return () => {
      cancelled = true;
      if (pdfDoc) { try { pdfDoc.destroy(); } catch (_) { /* noop */ } }
      pdfRef.current = null;
      if (containerRef.current) containerRef.current.innerHTML = "";
      if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
      canvasesRef.current = [];
    };
  }, [open, pdfUrl, wide]);

  const dl = async (it) => {
    if (it.onClick) { it.onClick(); return; }
    try {
      const r = await api.get(it.url, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = u; a.download = it.filename || "dokumen"; a.click();
      URL.revokeObjectURL(u);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openNewTab = () => { if (blobUrlRef.current) window.open(blobUrlRef.current, "_blank", "noopener"); };

  const buildJpegFiles = async () => {
    const base = slug(title);
    const files = [];
    const doc = pdfRef.current;
    const n = doc ? doc.numPages : 0;
    for (let i = 0; i < n; i++) {
      const blob = await renderFullHd(doc, i + 1);
      if (blob) {
        const name = n > 1 ? `${base}_hal${i + 1}.jpg` : `${base}.jpg`;
        files.push(new File([blob], name, { type: "image/jpeg" }));
      }
    }
    return files;
  };

  const downloadJpeg = async () => {
    const files = await buildJpegFiles();
    if (!files.length) return toast.error("Gambar belum siap");
    files.forEach((f) => { const u = URL.createObjectURL(f); const a = document.createElement("a"); a.href = u; a.download = f.name; a.click(); URL.revokeObjectURL(u); });
    toast.success(`${files.length} gambar JPEG diunduh`);
  };

  const shareWA = async () => {
    setSharing(true);
    try {
      const files = await buildJpegFiles();
      if (!files.length) { toast.error("Gambar belum siap"); return; }
      const waText = `Terlampir ${title}.`;
      if (navigator.canShare && navigator.canShare({ files })) {
        try { await navigator.share({ files, text: waText, title }); return; }
        catch (e) { if (e?.name === "AbortError") return; }
      }
      files.forEach((f) => { const u = URL.createObjectURL(f); const a = document.createElement("a"); a.href = u; a.download = f.name; a.click(); URL.revokeObjectURL(u); });
      toast.info("Gambar JPEG diunduh. WhatsApp akan terbuka — lampirkan gambar tersebut.", { duration: 7000 });
      window.open(`https://wa.me/?text=${encodeURIComponent(waText)}`, "_blank", "noopener");
    } finally { setSharing(false); }
  };

  const boxH = wide ? "h-[72vh]" : "h-[58vh]";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className={`${wide ? "max-w-6xl" : "max-w-4xl"} max-h-[94vh] overflow-hidden flex flex-col`} data-testid="export-preview-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">Pratinjau dokumen (JPEG) sebelum diunduh atau dibagikan ke WhatsApp.</DialogDescription>
        </DialogHeader>
        {toolbar && <div className="-mt-1">{toolbar}</div>}
        <div className={`relative flex-1 ${boxH} rounded-lg border border-slate-200 bg-slate-100 overflow-auto scroll-thin`}>
          {loading && (
            <div className={`absolute inset-0 flex ${boxH} items-center justify-center text-slate-400`}><Loader2 className="h-6 w-6 animate-spin mr-2" /> Memuat pratinjau…</div>
          )}
          {error && !loading && (
            <div className={`flex ${boxH} flex-col items-center justify-center gap-3 text-slate-500`}>
              <p className="text-sm">Gagal memuat pratinjau.</p>
              <Button onClick={openNewTab} data-testid="export-open-newtab-fallback" className="bg-blue-800 hover:bg-blue-900"><ExternalLink className="h-4 w-4 mr-1" /> Buka di Tab Baru</Button>
            </div>
          )}
          <div ref={containerRef} className="p-2" data-testid="export-preview-frame" />
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button className="bg-[#25D366] hover:bg-[#1ebe5b] text-white" onClick={shareWA} disabled={loading || sharing} data-testid="export-share-wa">
            {sharing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : (typeof navigator !== "undefined" && navigator.share ? <Share2 className="h-4 w-4 mr-1" /> : <MessageCircle className="h-4 w-4 mr-1" />)} Kirim ke WhatsApp
          </Button>
          <Button variant="outline" onClick={downloadJpeg} disabled={loading} data-testid="export-dl-jpeg"><ImageIcon className="h-4 w-4 mr-1" /> Unduh JPEG</Button>
          <Button variant="outline" onClick={openNewTab} data-testid="export-open-newtab"><ExternalLink className="h-4 w-4 mr-1" /> Tab Baru</Button>
          {onPrint && <Button variant="outline" onClick={onPrint} data-testid="export-print"><Printer className="h-4 w-4 mr-1" /> Print</Button>}
          {items.map((it, i) => (
            <Button key={i} onClick={() => dl(it)} data-testid={`export-dl-${i}`} variant="outline">
              <Download className="h-4 w-4 mr-1" /> {it.label}
            </Button>
          ))}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
